import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

/** Task findings already contain diagnostics; history is loaded only on demand. */
export function TaskFailureDetails({ taskId, details }: {
  taskId: string;
  details?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const history = useQuery<{
    ok: boolean;
    error?: string;
    runs?: Array<{ id: string; started_at: string; status: string; error?: string }>;
  }>({
    queryKey: ['task-failure-history', taskId],
    queryFn: () => api.mcp('tasks.task_history', { id: taskId, limit: 5 }),
    enabled: expanded,
    refetchInterval: expanded ? 60_000 : false,
  });
  const targets = Array.isArray(details?.targets) ? details.targets : [];
  return (
    <div className='mt-2 space-y-2 text-xs' data-testid='task-failure-details'>
      {typeof details?.last_error === 'string' && (
        <p className='text-amber-200 whitespace-pre-wrap break-words'>{details.last_error}</p>
      )}
      {typeof details?.node_id === 'string' && (
        <p className='text-gray-400 break-all'>{t('meshPeers.failureNode')}: {details.node_id}</p>
      )}
      {targets.map((target, index) => (
        <div key={index} className='font-mono text-gray-300 break-words'>
          {Object.entries(target ?? {}).filter(([, value]) =>
            typeof value === 'string'
          ).map(([key, value]) => <div key={key}>{key}: {String(value)}</div>)}
        </div>
      ))}
      {details?.targets_truncated === true && (
        <p>{t('meshPeers.failureTargetsLimited', { count: Number(details.failed_target_count) })}</p>
      )}
      <button type='button' className='text-blue-300 underline' aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}>
        {t('meshPeers.failureHistory')}
      </button>
      {expanded && (
        <div className='space-y-2 border-l border-gray-700 pl-2'>
          {history.isLoading && <p>{t('meshPeers.failureHistoryLoading')}</p>}
          {(history.isError || history.data?.ok === false) && (
            <p role='alert'>{history.error?.message ?? history.data?.error ?? t('meshPeers.failureHistoryError')}</p>
          )}
          {history.data?.runs?.map((run) => (
            <div key={run.id}>
              <p className='text-gray-400'>#{run.id} · {new Date(run.started_at).toLocaleString()} · {run.status}</p>
              {run.error && <p className='whitespace-pre-wrap break-words text-amber-200'>{run.error}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
